import {Card, DataStatus, Separator} from 'platform/components';
import {Box, Grid, Show} from 'platform/foundation';
import {useFormatPercentage} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerContractMaterialDiscountsQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

import {SummaryItem} from '../../SummaryItem/SummaryItem';

interface ContractMaterialDiscountsTabProps extends TestIdProps {
  customerContractId: string;
}

export function ContractMaterialDiscountsTab(props: ContractMaterialDiscountsTabProps) {
  const formatPercentage = useFormatPercentage();

  const {data, isLoading, isError} = useGetCustomerContractMaterialDiscountsQuery({
    customerContractId: props.customerContractId,
  });

  return (
    <DataStatus
      isLoading={isLoading}
      isError={isError}
      isEmpty={isNilOrEmpty(data?.materialDiscount)}
      minHeight={50}
    >
      <Card title={i18n.t('entity.customerContract.labels.materialDiscounts')}>
        {data?.materialDiscount?.map((discount, index) => (
          <Box key={discount?.id}>
            <Show when={index > 0}>
              <Separator />
            </Show>
            <Grid columns={4}>
              <SummaryItem
                isAlternative
                title={i18n.t('general.labels.name')}
                value={discount?.name}
                data-testid={suffixTestId('name', props)}
              />
              <SummaryItem
                isAlternative
                title={i18n.t('entity.customerContract.labels.discountType')}
                value={discount?.discountSettingsType?.name}
                data-testid={suffixTestId('discountType', props)}
              />
              <Show when={isNotNil(discount?.discountCustomerGroup)}>
                <SummaryItem
                  title={i18n.t('general.labels.discount')}
                  value={formatPercentage((discount?.discountCustomerGroup ?? 0) / 100)}
                  data-testid={suffixTestId('discountCustomerGroup', props)}
                />
              </Show>
              <Show when={isNotNil(discount?.discountOnSalePrice)}>
                <SummaryItem
                  title={i18n.t('general.labels.discount')}
                  value={formatPercentage((discount?.discountOnSalePrice ?? 0) / 100)}
                  data-testid={suffixTestId('discountOnSalePrice', props)}
                />
              </Show>
              <Show when={isNotNil(discount?.marginToPurchasePrice)}>
                <SummaryItem
                  title={i18n.t('general.labels.margin')}
                  value={formatPercentage((discount?.marginToPurchasePrice ?? 0) / 100)}
                  data-testid={suffixTestId('marginToPurchasePrice', props)}
                />
              </Show>
            </Grid>
          </Box>
        ))}
      </Card>
    </DataStatus>
  );
}
